import moment from "moment";
import { ICustomFieldValidation } from "src/admin/Organization/CustomFields/CustomFieldContainer";
import { concat } from "lodash";
import OrganizationAPI from "src/admin/Organization/API/OrganizationAPI";
import "src/extensions";
import { Assessment, CustomFieldValue } from "src/@types/ImpiricaPlatformApi";

export const Vitalsconfig = {
	server: "http://127.0.0.1",
	servicePort: 34512,
	dcatPort: 34513,
	// TODO: all properties below are not being used??
	pollInterval: 1000, // the interval between polls of the app
	REDIRECT_DELAY: 1000, // The delay between the app exiting and firing
	// the syncfinished/dcatfinished event (helps ensure
	// the server has finsihed processing data)
	launchTimeout: 15 * 1000, // The time to wait before the failed message appears
	currVisit: null, // Current VisitId
	isSyncing: false, // Are we currently syncing
	syncFailed: false, // Did the sync have failed items
	isDCATStarting: false, // Is the DCAT App launching?
	isDCATRunning: false, // We've heard back from the DCAT app, so now we're running
	isDCATCompleted: false, // Whether or not the currently running DCAT has been Completed (all tasks done)
	sessionId: null, // The current Session Id, used for authentication
	dcatPollPtr: null, // The Pointer to the Timeout which polls the DCAT App
};

export const breakpoints = {
	xs: 0,
	sm: 576,
	md: 768,
	lg: 992,
	xl: 1200,
	xxl: 1600,
};

export const errorCategories = {
	trainable: ["OT", "PS", "AG", "SP", "RS", "PR"],
	cognitive: ["CO", "AB", " P", "OB", "PT"],
};

export const months = [
	"January",
	"February",
	"March",
	"April",
	"May",
	"June",
	"July",
	"August",
	"September",
	"October",
	"November",
	"December",
];

/**
 * @param {number} month
 * @returns {array}
 * @description eg. input month =4 , returns [4,5,6,7,8,9,10,11,12,1,2,3]
 */
export const sortDateStartingFromCurrent = (month: number) => {
	const numericMonths = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11];
	if (month > 12) {
		month = month - 12;
	}
	const sortedMonth: number[] = [];
	const pastMonth: number[] = [];
	numericMonths.forEach((m) => {
		if (m - month < 0) {
			pastMonth.push(m);
		} else {
			sortedMonth.push(m);
		}
	});
	return concat(sortedMonth, pastMonth);
};

export const rowDateSortFunction = (rowA: any, rowB: any, desc: any) => {
	let a = rowA ? moment(rowA) : null;
	let b = rowB ? moment(rowB) : null;

	if (!a) return 1;
	if (!b) return -1;

	if (a > b) return 1;
	if (a < b) return -1;
	return 0;
};

export const truncateString = (description: string, limit: number = 20) => {
	if (description.length > limit) {
		return description.substring(0, limit) + "...";
	}
	return description;
};

export const getTime = (mode: string) => {
	const today = new Date();
	switch (mode) {
		case "today":
			return moment(today);
		case "thisYear":
			return moment(`${today.getFullYear()}-01-01`);
		case "lastYear":
			return moment(`${today.getFullYear() - 1}-01-01`);
		case "twoYearsBefore":
			return moment(`${today.getFullYear() - 2}-01-01`);
		case "thisMonth":
			return moment(today).format("YYYY-MM");
		case "lastMonth":
			return moment(today)
				.subtract(1, "month")
				.endOf("month")
				.format("YYYY-MM");
		case "twoMonthsBefore":
			return moment(today)
				.subtract(2, "month")
				.endOf("month")
				.format("YYYY-MM");
		case "date":
			return moment().format("YYYY-MM-DD");
		default:
			return moment(today);
	}
};

export const renderPercentage = (count: number, sum: number) =>
	Math.round((count / sum) * 10000) / 100 + "%";

export enum SecurityRoles {
	Administrator = "Admin",
	Customer = "Customer",
}

export type CommercialDrivingExpValueMapType = {
	value: number;
	description: string;
};

export const CommercialDrivingExpValueMap: CommercialDrivingExpValueMapType[] =
	[
		{ value: 0, description: "0" },
		{ value: 1, description: "1m" },
		{ value: 3, description: "3m" },
		{ value: 6, description: "6m" },
		{ value: 9, description: "9m" },
		{ value: 12, description: "1y" },
		{ value: 24, description: "2y" },
		{ value: 36, description: "3y" },
		{ value: 48, description: "4y" },
		{ value: 60, description: "5y" },
		{ value: 72, description: "6y" },
		{ value: 84, description: "7y" },
		{ value: 96, description: "8y" },
		{ value: 108, description: "9y" },
		{ value: 120, description: "10y" },
		{ value: 132, description: "11y" },
		{ value: 144, description: "12y" },
		{ value: 156, description: "13y" },
		{ value: 168, description: "14y" },
		{ value: 180, description: "15y" },
		{ value: 192, description: "16y" },
		{ value: 204, description: "17y" },
		{ value: 216, description: "18y" },
		{ value: 228, description: "19y" },
		{ value: 240, description: "20y" },
		{ value: 252, description: "21y" },
		{ value: 264, description: "22y" },
		{ value: 276, description: "23y" },
		{ value: 288, description: "24y" },
		{ value: 300, description: "25y" },
		{ value: 312, description: "26y" },
		{ value: 324, description: "27y" },
		{ value: 336, description: "28y" },
		{ value: 348, description: "29y" },
		{ value: 360, description: "30y +" },
	];

// Automatic info redaction values for organizations and datasets
export enum ClientAutoRedactionState {
	None = "None",
	Timed = "Timed",
	Inherit = "Inherit",
}

export interface IParameterValidation {
	validationStatus: "success" | "error" | "warning" | "validating" | undefined;
	message: string;
}

export const numericInputRegex = /^[0-9]*$/g;
export const alphabetsDashInputRegex = /^[a-zA-Z- ]*$/g;
export const alphaNumericDashInputRegex = /^[a-zA-Z- 0-9]*$/g;
export const alphaNumCommaDotDashInputRegex = /^[A-Za-z0-9\s,\.\-]*$/g;
export const birthYearRegex = /(?:19|20)\d\d/g;

const validationError = (message: string): IParameterValidation => {
	return { validationStatus: "error", message };
};

const validationWarning = (message: string): IParameterValidation => {
	return { validationStatus: "warning", message };
};

const validationSuccess = (message: string = ""): IParameterValidation => {
	return { validationStatus: "success", message };
};

const validationNoMessage = (): IParameterValidation => {
	return { validationStatus: undefined, message: "" };
};

export class Validation {
	public static validateOptionalEmail = (
		email: string | any
	): IParameterValidation => {
		if (!email) {
			return validationSuccess();
		}
		if (!emailRegex.test(email)) {
			return validationError("Please enter a valid email");
		}
		return validationSuccess();
	};

	public static validateEmail = async (
		email: string | any,
		isUnique?: boolean,
		isRequired?: boolean
	): Promise<IParameterValidation> => {
		if (isRequired === false && !email) {
			return validationSuccess();
		}

		if (email === undefined) {
			return validationError("Please enter a valid email");
		}

		if (email === "") {
			return validationError("Please enter a valid email");
		}

		// compare with regex first then check if it's existing email
		if (!emailRegex.test(email)) {
			return validationError("Please enter a valid email");
		}

		if (isUnique === true) {
			let inUse = await OrganizationAPI.getOrganizationEmailExists({
				email: encodeURIComponent(email),
			});
			if (inUse) {
				return validationError("Email is already in use");
			}
		}

		return validationSuccess();
	};

	public static validateBirthYear = ({
		birthYear,
	}: ClientDTO): IParameterValidation => {
		if (!birthYear) {
			return validationError("Please enter a birth year");
		}
		if (!birthYear.toString().match(birthYearRegex)) {
			return validationError("Please enter valid birth year");
		}
		if (birthYear > new Date().getFullYear() - 16) {
			return validationError("Client needs to be over 16 years old");
		}
		return validationSuccess();
	};

	public static validateAge = ({
		birthYear,
		birthMonth,
		birthDay,
	}: ClientDTO): IParameterValidation => {
		const dateSixteenYearsAgo = moment().subtract(16, "years");
		let date: moment.Moment;
		if (birthYear && birthMonth && birthDay) {
			date = moment(`${birthDay}-${birthMonth}-${birthYear}`, "DD-MM-YYYY");
			return date < dateSixteenYearsAgo
				? validationSuccess("")
				: validationError("Age must be greater than 16 years");
		} else if (birthYear && birthMonth) {
			date = moment(`${birthMonth}-${birthYear}`, "MM-YYYY");
			return date < dateSixteenYearsAgo
				? validationSuccess("")
				: validationError("Age must be greater than 16 years");
		} else if (birthYear) {
			return Validation.validateBirthYear({ birthYear });
		} else {
			return {
				validationStatus: undefined,
				message: "",
			} as IParameterValidation;
		}
	};

	public static validateErrorCode = (
		errorCode: string
	): IParameterValidation => {
		const fleetErrorRegex = /^[A-Z]{1,2}$/g;

		if (errorCode === "") {
			return validationError("Please enter a valid fleet error");
		}

		if (errorCode.length === 2 && errorCode[1] !== "X") {
			return validationError("Fleet Error with length 2 must end with X");
		}

		if (!errorCode.match(fleetErrorRegex)) {
			return validationError("Fleet Error has to be 1 or 2 capital letters");
		}

		return validationSuccess();
	};

	public static validateIfLessThanOrEqualTo = (
		num: number,
		standard: number,
		message: string
	): IParameterValidation => {
		if (num <= standard) {
			return validationSuccess();
		} else {
			return validationError(message);
		}
	};

	public static validateIsGreaterThan = (
		num: number,
		standard: number,
		message: string
	): IParameterValidation => {
		if (num > standard) {
			return validationSuccess();
		} else {
			return validationError(message);
		}
	};

	/*
		This function will take a value, and check whether or not it is empty or undefined. It will return an error
		with the passed in message, if the value is empty.
	*/
	public static validateStringIsNotEmpty = (
		value: string,
		message: string
	): IParameterValidation => {
		if (value === undefined || value.trim() === "") {
			return validationError(message);
		}
		return validationSuccess("Successful");
	};

	// checks whether a value exists in a given list, returns IParameterValidation object with the given message
	public static validateValueIsUnique =
		(list: string[]) =>
		(value: string, message: string): IParameterValidation => {
			const presentInList: boolean = list.includes(value.toLocaleLowerCase());
			if (presentInList) {
				return validationError(message);
			}
			return validationSuccess("Successful");
		};

	// takes in list of Validation Functions, a list of corresponding Error messages, and a value, and runs multiple Validations on that value
	// !! NOTE: validaitonFunction MUST have a signature of functionName(value: string, message: string): IParameterValidation { ... }
	public static runMultipleValidations = (
		listOfValidationFunctions: any[],
		listOfMessages: any[],
		value: any
	): IParameterValidation => {
		type ValidationFunction = (
			value: string,
			message: string
		) => IParameterValidation;
		return listOfValidationFunctions.reduce(
			(
				acc: IParameterValidation,
				validationFunction: ValidationFunction,
				i
			) => {
				if (acc.validationStatus === "error") {
					// if an error has already occured, no need to run the next validation
					return acc;
				}
				// otherwise, we need to run the next validation Function and see its output.
				const validationOutcome: IParameterValidation = validationFunction(
					value,
					listOfMessages[i]
				);
				return validationOutcome;
			},
			{} as IParameterValidation
		);
	};

	public static validateArrayIsNotEmpty = (
		value: any[],
		message: string
	): IParameterValidation => {
		if (value !== undefined && value.length > 0) {
			return validationSuccess();
		}
		return validationError(message);
	};

	public static validatePhoneNumber = (
		phoneNumber: string
	): IParameterValidation => {
		let validValue = validationError("Please enter a valid phone number ");
		const phoneNumberValidation =
			/^\(?([0-9]{3})\)?[-. ]?([0-9]{3})[-. ]?([0-9]{4})$/;
		if (phoneNumber) {
			if (phoneNumber.length < 10) {
				validValue = validationWarning("Please enter a 10 digit phone number");
			}
			if (phoneNumberValidation.test(phoneNumber)) {
				validValue = validationSuccess();
			}
		}
		return validValue;
	};
	/* used to set daysUntilredaction, DaysUntilCoreLocked fields to show no
	validation when set to none and error if set to timed if passing in a bool */
	public static validateIfANumberOrNumbers = (
		values: number | number[],
		setValue: boolean | undefined = undefined
	): IParameterValidation => {
		let validValues = {} as IParameterValidation;
		if (setValue !== undefined) {
			if (setValue === true) {
				if (!Array.isArray(values) && isNaN(values)) {
					validValues = validationNoMessage();
				}
			}
			if (setValue === false) {
				if (!Array.isArray(values) && isNaN(values)) {
					validValues = validationError("Please enter a value!");
				}
			}
		}
		if (setValue === undefined) {
			if (Array.isArray(values)) {
				if (values.length > 0 !== true) {
					validValues = validationError("Please choose an option ");
				} else {
					validValues = validationSuccess();
				}
			} else {
				if (values === undefined) {
					validValues = validationError("Please choose an option");
				} else {
					validValues = validationSuccess();
				}
			}
		}

		return validValues;
	};

	/* used to validate a number within a range */
	public static validateNumberRange = (
		value: number,
		minimum: number,
		maximum: number
	): IParameterValidation => {
		let validValues: IParameterValidation;
		if (value >= minimum && value <= maximum) {
			return validationSuccess();
		} else {
			return validationError("Value must be within range");
		}
	};

	/* used to set daysUntilredaction, DaysUntilCoreLocked fields to show no
	validation when set to none and error if set to timed if passing in a bool */
	public static validateIsEnum = (
		values: number | number[],
		setValue: string | undefined = undefined
	): IParameterValidation => {
		let validValues = {} as IParameterValidation;
		if (setValue !== undefined) {
			if (
				setValue === ClientAutoRedactionState.None ||
				setValue === ClientAutoRedactionState.Inherit
			) {
				if (!Array.isArray(values) && isNaN(values)) {
					validValues = validationNoMessage();
				}
			}
			if (setValue === ClientAutoRedactionState.Timed) {
				if (!Array.isArray(values) && isNaN(values)) {
					validValues = validationError("Please enter a value!");
				}
			}
		}

		return validValues;
	};

	public static validateIsActive = (
		isActive: boolean
	): IParameterValidation => {
		if (typeof isActive !== "boolean" || isActive === undefined) {
			return { validationStatus: "error", message: "Please choose a value" };
		}

		return { validationStatus: "success", message: "" };
	};

	public static validateTraining = (
		training: boolean
	): IParameterValidation => {
		if (typeof training !== "boolean" || training === undefined) {
			return { validationStatus: "error", message: "Please choose a value" };
		}

		return { validationStatus: "success", message: "" };
	};

	public static validateAddress = (address1: string): IParameterValidation => {
		let validateAddress = {
			validationStatus: "error",
			message: "Please enter an address",
		};
		if (address1 && address1 !== "" && address1.length > 0) {
			validateAddress = { validationStatus: "success", message: "" };
		}
		return validateAddress as IParameterValidation;
	};

	public static validatePostalCode = (
		postalCode: string,
		countryPickListId: number
	): IParameterValidation => {
		let isCanada: boolean = countryPickListId === 10059 ? true : false;
		let validatePostalCode = {
			validationStatus: "error",
			message: isCanada
				? "Please enter a valid postal code"
				: "Please enter a valid zip code",
		};
		// Check format of postal code...
		if (isCanada) {
			let regexp =
				/^[ABCEGHJ-NPRSTVXY]\d[ABCEGHJ-NPRSTV-Z][ -]?\d[ABCEGHJ-NPRSTV-Z]\d$/i;
			if (regexp.test(postalCode) === true) {
				validatePostalCode = { validationStatus: "success", message: "" };
			}
		} else {
			let regexp = /^[0-9]{5}(?:-[0-9]{4})?$/;
			if (regexp.test(postalCode) === true) {
				validatePostalCode = { validationStatus: "success", message: "" };
			}
		}
		return validatePostalCode as IParameterValidation;
	};

	public static validateIfCombinationAlreadyExists = (
		existingValues: FleetErrorDTO[],
		code: string,
		category: number,
		fleetErrorId: number
	) => {
		const validationObject = {
			errorCode: { isActive: validationError("Please enter a value") },
			skillCategory: { isActive: validationError("Please choose a value") },
		};

		validationObject.errorCode = {
			isActive: Validation.validateErrorCode(code),
		};

		if (category !== undefined) {
			validationObject.skillCategory = { isActive: validationSuccess() };
		}

		if (
			existingValues.some(
				(val: FleetErrorDTO) =>
					val.skillCategory === category &&
					val.errorCode === code &&
					val.id !== fleetErrorId
			)
		) {
			validationObject.errorCode = {
				isActive: validationError(
					"An error code under with this skill category exists. Choose a different error code "
				),
			};
			validationObject.skillCategory = {
				isActive: validationError(
					"An error code exists with this skill category! Choose another category"
				),
			};
		}

		return validationObject;
	};

	public static isValid = (validProperties: string[]) => {
		return validProperties.every(
			(prop) => prop === "success" || prop === undefined
		);
	};

	public static validateCustomFields = (
		customFields: CustomFieldDTO[],
		customFieldValues?: CustomFieldValueDTO[]
	) => {
		if (customFieldValues !== undefined && customFieldValues.length > 0) {
			return customFields.map((f) => {
				const foundField = customFieldValues.find(
					(cfv) => cfv.fieldId === f.id
				);
				if (foundField !== undefined) {
					if (
						f.required &&
						(foundField === undefined ||
							foundField.value === undefined ||
							foundField.value === "")
					) {
						return {
							fieldId: f.id,
							fieldName: f.name,
							validation: {
								validationStatus: "error",
								message: "Field required.",
							},
						};
					} else {
						return {
							fieldId: f.id,
							fieldName: f.name,
							validation: { validationStatus: "success", message: "" },
						};
					}
				} else {
					return {
						fieldId: null,
						fieldName: f.name,
						validation: {
							validationStatus: "error",
							message: "Field required.",
						},
					};
				}
			});
		} else {
			return [];
		}
	};

	public static validateNewCustomFields = (
		customFields: CustomFieldDTO[],
		customFieldValues?: CustomFieldValueDTO[]
	) => {
		if (customFieldValues !== undefined && customFieldValues.length > 0) {
			return customFields.map((f) => {
				const foundField = customFieldValues.find(
					(cfv) => cfv.fieldId === f.id
				);
				if (foundField !== undefined) {
					if (
						f.required &&
						(foundField === undefined ||
							foundField.value === undefined ||
							foundField.value === "")
					) {
						return {
							fieldId: f.id,
							name: f.name,
							validationStatus: "error",
							message: "Field required.",
						} as ICustomFieldValidation;
					} else {
						return {
							name: f.name,
							validationStatus: "success",
							message: "",
						} as ICustomFieldValidation;
					}
				} else {
					return {
						name: f.name,
						validationStatus: "success",
						message: "",
					} as ICustomFieldValidation;
				}
			});
		} else {
			return [];
		}
	};

	public static validateCustomFieldHasOneOption = (
		options: any,
		controlTypeId: number
	): IParameterValidation => {
		const notUndefinedOrEmpty = (currentValue: CustomFieldOptionsData) =>
			currentValue.options === undefined || currentValue.options === "";

		if (
			controlTypeId &&
			controlTypeId !== 10236 &&
			options.every(notUndefinedOrEmpty)
		) {
			return validationError("Please add at least one option");
		}
		return { validationStatus: undefined, message: "" };
	};

	public static isValidIncludingCustomFields = (
		validDetails: any,
		customFieldValidationKeyName: string
	) => {
		const customFieldValdation =
			validDetails.customFieldValues !== undefined &&
			validDetails.customFieldValues.length > 0
				? validDetails.customFieldValues.map((val: string) => val)
				: [];
		const otherValidProperties = Object.keys(validDetails) // validation comes as success, error and occasionally undefined
			.filter((key) => key !== customFieldValidationKeyName)
			.map((key) => validDetails[key].validationStatus)
			.filter((value: string) => value !== undefined);
		const isValid = [...otherValidProperties, ...customFieldValdation];
		return isValid.every((v) => v === "success");
	};
}

export interface InputError {
	hasError: boolean;
	message: string;
}

// tslint:disable
export const emailRegex =
	/^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
// tslint:enable

export class Utils {
	// tslint:disable
	public static generateUUID = () => {
		let d = new Date().getTime();
		return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
			const r = (d + Math.random() * 16) % 16 | 0;
			d = Math.floor(d / 16);
			return (c == "x" ? r : (r & 0x3) | 0x8).toString(16);
		});
	};
	// tslint:enable

	// https://gist.github.com/bsara/c60dd6bbb04e0969221f607f0df68716
	public static getScrollbarWidth = () => {
		const outer = document.createElement("div");
		const inner = document.createElement("div");

		outer.style.visibility = "hidden";
		outer.style.width = "100px";
		inner.style.width = "100%";

		outer.appendChild(inner);
		document.body.appendChild(outer);

		const widthWithoutScrollbar = outer.offsetWidth;

		outer.style.overflow = "scroll";

		const widthWithScrollbar = inner.offsetWidth;

		document.body.removeChild(outer);

		return widthWithoutScrollbar - widthWithScrollbar;
	};

	public static setDocTitle = (value: string | undefined) => {
		const titleSuffix = "ExceleRATE";
		if (value) {
			document.title = `${value} | ${titleSuffix}`;
		} else {
			document.title = titleSuffix;
		}
	};

	public static shouldMarkError = (error: InputError, touched: boolean) => {
		const hasError = error.hasError;
		const shouldShow = touched;
		return hasError ? shouldShow : false;
	};

	/** Extracts a value from an HTMLInputElement provided by a React.ChangeEvent */
	public static extractKeyValueFromChangeEvent = (
		event: React.ChangeEvent<HTMLInputElement>
	): { name: string; value: any } => {
		let value: any = event.target.value;
		if (value === "true") {
			value = true;
		}
		if (value === "false") {
			value = false;
		}
		let name: any = event.target.name;
		if (event.target.type === "radio") {
			//do nothing
		} else if (event.target.type === "checkbox") {
			if (event.target.dataset != null) {
				// Don't split if single value
				if (value === "") {
					value = [];
				} else {
					value = value.split(",");
				}

				name = event.target.dataset.name;
			} else {
				value = event.target.checked;
			}
		}

		return {
			name,
			value,
		};
	};

	public static getCommercialDrivingExpDesc = (value: number) => {
		const desc = CommercialDrivingExpValueMap.find((x) => x.value === value);
		if (desc) {
			return desc.description;
		} else {
			return "";
		}
	};

	public static getCommercialDrivingExpValue = (description: string) => {
		const val = CommercialDrivingExpValueMap.find(
			(x) => x.description === description
		);
		if (val) {
			return val.value;
		} else {
			return undefined;
		}
	};

	public static formatComment = (comments: string) => {
		return comments.replace(/[\n"]/g, (match) => {
			if (match === "\n") {
				return " "; // Replace new line characters
			} else if (match === '"') {
				return ""; // Remove double quotes
			} else {
				return match;
			}
		});
	};

	// Static function that maps CustomFieldValueDTO to CustomFieldValue
	public static mapCustomFieldValues = (
		customFieldValues: CustomFieldValueDTO[]
	): CustomFieldValue[] => {
		return customFieldValues.map(
			(cfv) =>
				new CustomFieldValue({
					id: cfv.id ? cfv.id : 0,
					clientId: cfv.clientId,
					customFieldId: cfv.fieldId ? cfv.fieldId : 0,
					coreTestResultId: cfv.coreTestResultId,
					assessmentResultId: cfv.assessmentResultId,
					vitalsTestResultId: cfv.vitalsTestResultId,
					careTestResultId: cfv.careTestResultId,
					value: cfv.value,
				})
		);
	};

	public static mapClientSiteAssessmentResult = (
		assessment: Assessment
	): ClientSiteAssessmentResultsDTO => {
		const mappedAssessment: ClientSiteAssessmentResultsDTO = {
			...assessment,
			dateCreated: assessment.dateCreated
				? assessment.dateCreated.toISOString()
				: undefined,
			dateModified: assessment.dateModified
				? assessment.dateModified.toISOString()
				: undefined,
			dateCompleted: assessment.dateCompleted
				? assessment.dateCompleted.toISOString()
				: undefined,
			vitalsStatusId: assessment.vitalsStatusId as
				| VitalsScoringDecisionType
				| undefined,
			cErrorStatusId: assessment.cErrorStatusId as
				| CoreScoringDecisionType
				| undefined,
			tErrorStatusId: assessment.tErrorStatusId as
				| CoreScoringDecisionType
				| undefined,
			appraisalStatusId: assessment.appraisalStatusId as
				| CoreScoringDecisionType
				| undefined,
			neurapulseCompleted: assessment.neurapulseCompleted
				? assessment.neurapulseCompleted.toISOString()
				: undefined,
			neurapulseRecommendation: assessment.neurapulseRecommendation as
				| NeurapulseRecommendationType
				| undefined,
		};
		return mappedAssessment;
	};

	public static mapNeurapulseRecommendationType = (
		value?: number
	): NeurapulseRecommendationType | undefined => {
		switch (value) {
			case 0:
				return "Completed";
			case 1:
				return "Incomplete";
			case 2:
				return "HighRisk";
			default:
				return undefined;
		}
	};
}

export default Utils;
